import { useEffect, useRef, useState } from 'react';

/**
 * useInvalidUntilFocus — turns the parent's `invalid` flag into a "show the error
 * until the user clicks back into this field" signal.
 *
 * WHY. The wizard marks a field `invalid` when the user tries to advance past an
 * incomplete/incorrect entry (see `triedStage` in useUploadOverlay). That flag is
 * owned by the parent and would otherwise stay on until the parent recomputes it —
 * which only happens on the NEXT advance attempt. The design we want is: the field
 * goes red and STAYS red (through hover, through typing elsewhere) until the user
 * returns to THIS field and focuses it. Clicking back in is the acknowledgement
 * that clears the red.
 *
 * HOW. We latch a local `acknowledged` bit the first time the user MOUSES into the
 * control — a mousedown/pointerdown on the field, i.e. hover-then-click, which is
 * exactly the interaction asked for ("hover your mouse over it AND click back in").
 * A plain `focus` is deliberately NOT the trigger: tabbing to the field with the
 * keyboard would otherwise silently clear the red without the user ever pointing at
 * it. While `invalid` is true and not yet acknowledged, the field shows its error
 * state. If the parent later flips `invalid` back on — a fresh failed advance — we
 * re-arm (clear the latch) so the field reddens again. When the parent says the
 * field is valid, there is nothing to show and the latch resets.
 *
 * Returns { showInvalid, onPointerDown } — spread `onPointerDown` onto the input/
 * select/textarea, and drive the border + label colour from `showInvalid`.
 */
export function useInvalidUntilFocus(invalid: boolean | undefined) {
  const isInvalid = Boolean(invalid);
  const [acknowledged, setAcknowledged] = useState(false);
  const prevInvalid = useRef(isInvalid);

  // Re-arm on a fresh false->true transition (a new failed advance attempt), and
  // reset once the parent clears the error so a later error starts un-acknowledged.
  useEffect(() => {
    if (isInvalid && !prevInvalid.current) setAcknowledged(false);
    if (!isInvalid) setAcknowledged(false);
    prevInvalid.current = isInvalid;
  }, [isInvalid]);

  return {
    showInvalid: isInvalid && !acknowledged,
    // Mouse press on the field = hover + click. This is what clears the red.
    onPointerDown: () => setAcknowledged(true),
  };
}
